import '../App.css';
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import authgear from "../authgear";
import Header from "../components/Header";

const base_url = process.env.NODE_ENV === "production"
    ? "https://hooks-28xd.onrender.com"
    : "http://localhost:4000";

const card = {
    background: "#1a1a2e",
    border: "1px solid #2a2a4a",
    borderRadius: "10px",
    padding: "20px",
};

const MarketingAgent = () => {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [selectedId, setSelectedId] = useState("");
    const [project, setProject] = useState(null);
    const [planDraft, setPlanDraft] = useState("");
    const [planSaved, setPlanSaved] = useState(false);
    const [savingPlan, setSavingPlan] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [running, setRunning] = useState(false);
    const [runMessage, setRunMessage] = useState("");
    const [showOneOffForm, setShowOneOffForm] = useState(false);
    const [oneOffRequest, setOneOffRequest] = useState("");

    // New project form
    const [showNewForm, setShowNewForm] = useState(false);
    const [newName, setNewName] = useState("");
    const [newDesc, setNewDesc] = useState("");
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState("");

    const [jobs, setJobs] = useState([]);

    // In-UI approval state
    const [approvingId, setApprovingId] = useState(null);
    const [updateFormId, setUpdateFormId] = useState(null);
    const [updateText, setUpdateText] = useState("");
    const [updatingId, setUpdatingId] = useState(null);
    const [deletingJobId, setDeletingJobId] = useState(null);
    const [approvingMediaId, setApprovingMediaId] = useState(null);
    const [cancellingId, setCancellingId] = useState(null);

    const authHeaders = useCallback(async () => {
        await authgear.refreshAccessTokenIfNeeded();
        return { Authorization: `Bearer ${authgear.accessToken}` };
    }, []);

    const fetchProjects = useCallback(async () => {
        try {
            const headers = await authHeaders();
            const { data } = await axios.get(`${base_url}/marketing/projects`, { headers });
            setProjects(data);
        } catch (_) {}
    }, [authHeaders]);

    const fetchJobs = useCallback(async (projectId) => {
        if (!projectId) { setJobs([]); return; }
        try {
            const headers = await authHeaders();
            const url = projectId === "__orphaned__"
                ? `${base_url}/marketing/jobs/orphaned`
                : `${base_url}/marketing/jobs`;
            const params = projectId === "__orphaned__" ? {} : { projectId };
            const { data } = await axios.get(url, { headers, params });
            setJobs(data);
        } catch (_) {}
    }, [authHeaders]);

    useEffect(() => {
        if (authgear.sessionState !== "AUTHENTICATED") {
            navigate("/login");
            return;
        }
        fetchProjects();
    }, [navigate, fetchProjects]);

    // Load full project and its jobs when selection changes
    useEffect(() => {
        setShowOneOffForm(false);
        setOneOffRequest("");
        if (!selectedId) {
            setProject(null);
            setPlanDraft("");
            setJobs([]);
            return;
        }
        if (selectedId === "__orphaned__") {
            setProject(null);
            setPlanDraft("");
            fetchJobs("__orphaned__");
            return;
        }
        const load = async () => {
            try {
                const headers = await authHeaders();
                const { data } = await axios.get(`${base_url}/marketing/projects/${selectedId}`, { headers });
                setProject(data);
                setPlanDraft(data.plan || "");
                setPlanSaved(false);
            } catch (_) {}
        };
        load();
        fetchJobs(selectedId);
    }, [selectedId, authHeaders, fetchJobs]);

    const handleCreateProject = async (e) => {
        e.preventDefault();
        if (!newName.trim() || creating) return;
        setCreating(true);
        setCreateError("");
        try {
            const headers = await authHeaders();
            const { data } = await axios.post(`${base_url}/marketing/projects`, {
                name: newName.trim(),
                description: newDesc.trim(),
            }, { headers });
            await fetchProjects();
            setSelectedId(data._id);
            setShowNewForm(false);
            setNewName("");
            setNewDesc("");
        } catch (err) {
            setCreateError(err.response?.data?.message || err.message);
        } finally {
            setCreating(false);
        }
    };

    const handleDeleteProject = async () => {
        if (!selectedId || deleting) return;
        if (!window.confirm(`Delete "${project.name}"? This cannot be undone.`)) return;
        setDeleting(true);
        try {
            const headers = await authHeaders();
            await axios.delete(`${base_url}/marketing/projects/${selectedId}`, { headers });
            setSelectedId("");
            setProject(null);
            setPlanDraft("");
            await fetchProjects();
        } catch (_) {}
        finally {
            setDeleting(false);
        }
    };

    const handleRunNow = async () => {
        if (!selectedId || running) return;
        setRunning(true);
        setRunMessage("");
        try {
            const headers = await authHeaders();
            const body = oneOffRequest.trim() ? { oneOffRequest: oneOffRequest.trim() } : {};
            await axios.post(`${base_url}/marketing/projects/${selectedId}/run`, body, { headers });
            setRunMessage("Run started — check email for approval link.");
            setShowOneOffForm(false);
            setOneOffRequest("");
            setTimeout(() => setRunMessage(""), 6000);
            setTimeout(() => fetchJobs(selectedId), 2000);
        } catch (err) {
            setRunMessage(err.response?.data?.message || err.message);
            setTimeout(() => setRunMessage(""), 6000);
        } finally {
            setRunning(false);
        }
    };

    const handleRunPreview = async () => {
        if (!selectedId || running) return;
        setRunning(true);
        setRunMessage("");
        try {
            const headers = await authHeaders();
            const body = oneOffRequest.trim() ? { oneOffRequest: oneOffRequest.trim() } : {};
            await axios.post(`${base_url}/marketing/projects/${selectedId}/run`, body, { headers });
            setRunMessage("Proposal ready — review below.");
            setShowOneOffForm(false);
            setOneOffRequest("");
            setTimeout(() => setRunMessage(""), 6000);
            await fetchJobs(selectedId);
        } catch (err) {
            setRunMessage(err.response?.data?.message || err.message);
            setTimeout(() => setRunMessage(""), 6000);
        } finally {
            setRunning(false);
        }
    };

    const handleApproveUI = async (jobId) => {
        setApprovingId(jobId);
        try {
            const headers = await authHeaders();
            await axios.post(`${base_url}/marketing/jobs/${jobId}/approve-ui`, {}, { headers });
            await fetchJobs(selectedId);
        } catch (err) {
            alert(err.response?.data?.message || err.message);
        } finally {
            setApprovingId(null);
        }
    };

    const handleRequestUpdateUI = async (jobId) => {
        if (!updateText.trim()) return;
        setUpdatingId(jobId);
        try {
            const headers = await authHeaders();
            await axios.post(`${base_url}/marketing/jobs/${jobId}/update-ui`, { updateRequest: updateText }, { headers });
            setUpdateFormId(null);
            setUpdateText("");
            setTimeout(() => fetchJobs(selectedId), 3000);
        } catch (err) {
            alert(err.response?.data?.message || err.message);
        } finally {
            setUpdatingId(null);
        }
    };

    const handleDeleteJob = async (jobId) => {
        setDeletingJobId(jobId);
        try {
            const headers = await authHeaders();
            await axios.delete(`${base_url}/marketing/jobs/${jobId}`, { headers });
            await fetchJobs(selectedId);
            if (project && selectedId !== "__orphaned__") {
                const { data } = await axios.get(`${base_url}/marketing/projects/${selectedId}`, { headers });
                setProject(data);
                setPlanDraft(data.plan || "");
            }
        } catch (err) {
            alert(err.response?.data?.message || err.message);
        } finally {
            setDeletingJobId(null);
        }
    };

    const handleApproveMediaUI = async (jobId) => {
        setApprovingMediaId(jobId);
        try {
            const headers = await authHeaders();
            await axios.post(`${base_url}/marketing/jobs/${jobId}/approve-media-ui`, {}, { headers });
            await fetchJobs(selectedId);
        } catch (err) {
            alert(err.response?.data?.message || err.message);
        } finally {
            setApprovingMediaId(null);
        }
    };

    const handleCancelJob = async (jobId) => {
        setCancellingId(jobId);
        try {
            const headers = await authHeaders();
            await axios.post(`${base_url}/marketing/jobs/${jobId}/cancel`, {}, { headers });
            await fetchJobs(selectedId);
        } catch (err) {
            alert(err.response?.data?.message || err.message);
        } finally {
            setCancellingId(null);
        }
    };

    const handleSavePlan = async () => {
        if (!selectedId || savingPlan) return;
        setSavingPlan(true);
        setPlanSaved(false);
        try {
            const headers = await authHeaders();
            await axios.put(`${base_url}/marketing/projects/${selectedId}`, { plan: planDraft }, { headers });
            setPlanSaved(true);
            setTimeout(() => setPlanSaved(false), 2500);
        } catch (_) {}
        finally {
            setSavingPlan(false);
        }
    };

    return (
        <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#ddd", fontFamily: "Verdana, sans-serif" }}>
            <Header />
            <div style={{ padding: "60px 20px 20px", maxWidth: "860px", margin: "0 auto" }}>
                <h2 style={{ color: "#a855f7", margin: "0 0 24px" }}>Management Agent</h2>

                {/* Project selector */}
                <div style={card}>
                    <label style={{ fontSize: "13px", color: "#888", display: "block", marginBottom: "8px" }}>
                        Project
                    </label>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                        <select
                            value={selectedId}
                            onChange={e => {
                                setShowNewForm(false);
                                setSelectedId(e.target.value);
                            }}
                            style={{
                                flex: 1,
                                minWidth: "200px",
                                background: "#0d0d1a",
                                border: "1px solid #2a2a4a",
                                borderRadius: "6px",
                                color: "#ddd",
                                padding: "8px 10px",
                                fontSize: "13px",
                            }}
                        >
                            <option value="">— Select a project —</option>
                            {projects.map(p => (
                                <option key={p._id} value={p._id}>{p.name}</option>
                            ))}
                            <option value="__orphaned__">Orphaned</option>
                        </select>
                        <button
                            onClick={() => { setSelectedId(""); setShowNewForm(v => !v); }}
                            style={{
                                padding: "8px 16px",
                                background: showNewForm ? "#2a2a4a" : "transparent",
                                color: showNewForm ? "#a855f7" : "#555",
                                border: `1px solid ${showNewForm ? "#a855f7" : "#2a2a4a"}`,
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontSize: "13px",
                                whiteSpace: "nowrap",
                            }}
                        >
                            + New Project
                        </button>
                    </div>
                </div>

                {/* New project form */}
                {showNewForm && (
                    <div style={{ ...card, marginTop: "12px" }}>
                        <div style={{ fontSize: "13px", color: "#888", marginBottom: "12px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            New Project
                        </div>
                        <form onSubmit={handleCreateProject}>
                            <input
                                value={newName}
                                onChange={e => setNewName(e.target.value)}
                                placeholder="Project name"
                                style={{
                                    width: "100%",
                                    background: "#0d0d1a",
                                    border: "1px solid #2a2a4a",
                                    borderRadius: "6px",
                                    color: "#ddd",
                                    padding: "8px 10px",
                                    fontSize: "13px",
                                    boxSizing: "border-box",
                                    marginBottom: "8px",
                                }}
                            />
                            <input
                                value={newDesc}
                                onChange={e => setNewDesc(e.target.value)}
                                placeholder="Short description (optional)"
                                style={{
                                    width: "100%",
                                    background: "#0d0d1a",
                                    border: "1px solid #2a2a4a",
                                    borderRadius: "6px",
                                    color: "#ddd",
                                    padding: "8px 10px",
                                    fontSize: "13px",
                                    boxSizing: "border-box",
                                    marginBottom: "12px",
                                }}
                            />
                            {createError && (
                                <div style={{ fontSize: "12px", color: "#f87171", marginBottom: "8px" }}>{createError}</div>
                            )}
                            <button
                                type="submit"
                                disabled={creating || !newName.trim()}
                                style={{
                                    padding: "8px 20px",
                                    background: creating || !newName.trim() ? "#2a2a4a" : "#a855f7",
                                    color: creating || !newName.trim() ? "#555" : "#fff",
                                    border: "none",
                                    borderRadius: "6px",
                                    cursor: creating || !newName.trim() ? "not-allowed" : "pointer",
                                    fontWeight: "bold",
                                    fontSize: "13px",
                                }}
                            >
                                {creating ? "Creating..." : "Create Project"}
                            </button>
                        </form>
                    </div>
                )}

                {/* Marketing plan editor */}
                {project && (
                    <div style={{ ...card, marginTop: "16px" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px", flexWrap: "wrap", gap: "8px" }}>
                            <div>
                                <div style={{ fontSize: "16px", color: "#ddd", fontWeight: "bold" }}>{project.name}</div>
                                {project.description && (
                                    <div style={{ fontSize: "12px", color: "#555", marginTop: "2px" }}>{project.description}</div>
                                )}
                            </div>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <button
                                    onClick={handleRunNow}
                                    disabled={running}
                                    style={{
                                        padding: "5px 12px",
                                        background: running ? "#2a2a4a" : "#a855f7",
                                        color: running ? "#555" : "#fff",
                                        border: "none",
                                        borderRadius: "6px",
                                        cursor: running ? "not-allowed" : "pointer",
                                        fontSize: "12px",
                                        fontWeight: "bold",
                                    }}
                                >
                                    {running ? "Running..." : "Run Now"}
                                </button>
                                <button
                                    onClick={handleRunPreview}
                                    disabled={running}
                                    style={{
                                        padding: "5px 12px",
                                        background: "transparent",
                                        color: running ? "#555" : "#a855f7",
                                        border: "1px solid #a855f7",
                                        borderRadius: "6px",
                                        cursor: running ? "not-allowed" : "pointer",
                                        fontSize: "12px",
                                        fontWeight: "bold",
                                    }}
                                >
                                    {running ? "Running..." : "Run & Preview"}
                                </button>
                                <button
                                    onClick={() => { setShowOneOffForm(v => !v); setOneOffRequest(""); }}
                                    style={{
                                        padding: "5px 12px",
                                        background: showOneOffForm ? "#1e1040" : "transparent",
                                        color: showOneOffForm ? "#a855f7" : "#555",
                                        border: `1px solid ${showOneOffForm ? "#a855f7" : "#2a2a4a"}`,
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        fontSize: "12px",
                                    }}
                                >
                                    One-off
                                </button>
                                <button
                                    onClick={handleDeleteProject}
                                    disabled={deleting}
                                    style={{
                                        padding: "5px 12px",
                                        background: "transparent",
                                        color: deleting ? "#555" : "#f87171",
                                        border: "1px solid #4a2a2a",
                                        borderRadius: "6px",
                                        cursor: deleting ? "not-allowed" : "pointer",
                                        fontSize: "12px",
                                    }}
                                >
                                    {deleting ? "Deleting..." : "Delete Project"}
                                </button>
                            </div>
                        </div>

                        {showOneOffForm && (
                            <div style={{ marginTop: "10px" }}>
                                <textarea
                                    value={oneOffRequest}
                                    onChange={e => setOneOffRequest(e.target.value)}
                                    rows={3}
                                    placeholder="Describe a specific action to take instead of letting the agent decide autonomously..."
                                    style={{
                                        width: "100%",
                                        background: "#0d0d1a",
                                        border: "1px solid #a855f7",
                                        borderRadius: "6px",
                                        color: "#ddd",
                                        padding: "8px 10px",
                                        fontSize: "12px",
                                        resize: "vertical",
                                        boxSizing: "border-box",
                                    }}
                                />
                            </div>
                        )}

                        {runMessage && (
                            <div style={{ fontSize: "12px", color: "#4ecdc4", marginTop: "8px" }}>{runMessage}</div>
                        )}

                        <div style={{ borderTop: "1px solid #2a2a4a", margin: "14px 0" }} />

                        <label style={{ fontSize: "13px", color: "#888", display: "block", marginBottom: "8px" }}>
                            Project Context
                        </label>
                        <textarea
                            value={planDraft}
                            onChange={e => { setPlanDraft(e.target.value); setPlanSaved(false); }}
                            rows={14}
                            placeholder={"Describe the project goals, current state, priorities, and any constraints. The agent will decide what to do next."}
                            style={{
                                width: "100%",
                                background: "#0d0d1a",
                                border: "1px solid #2a2a4a",
                                borderRadius: "6px",
                                color: "#ddd",
                                padding: "10px",
                                fontSize: "13px",
                                resize: "vertical",
                                boxSizing: "border-box",
                                lineHeight: "1.6",
                            }}
                        />
                        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "12px" }}>
                            <button
                                onClick={handleSavePlan}
                                disabled={savingPlan}
                                style={{
                                    padding: "8px 20px",
                                    background: savingPlan ? "#2a2a4a" : "#a855f7",
                                    color: savingPlan ? "#555" : "#fff",
                                    border: "none",
                                    borderRadius: "6px",
                                    cursor: savingPlan ? "not-allowed" : "pointer",
                                    fontWeight: "bold",
                                    fontSize: "13px",
                                }}
                            >
                                {savingPlan ? "Saving..." : "Save Plan"}
                            </button>
                            {planSaved && (
                                <span style={{ fontSize: "12px", color: "#4ecdc4" }}>Saved</span>
                            )}
                        </div>
                    </div>
                )}

                {selectedId === "__orphaned__" && jobs.length === 0 && (
                    <div style={{ ...card, marginTop: "16px", fontSize: "13px", color: "#555" }}>
                        No orphaned jobs found.
                    </div>
                )}

                {jobs.length > 0 && (
                    <div style={{ ...card, marginTop: "16px" }}>
                        <div style={{ fontSize: "13px", color: "#888", marginBottom: "12px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            {selectedId === "__orphaned__" ? "Orphaned Jobs" : "Recent Jobs"}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            {jobs.map(j => {
                                let action = null;
                                try { action = j.proposedAction ? JSON.parse(j.proposedAction) : null; } catch (_) {}
                                const isPending = j.status === "pending_approval";
                                const statusColor =
                                    j.status === "complete" ? "#4ecdc4" :
                                    j.status === "failed" ? "#f87171" :
                                    j.status === "cancelled" ? "#555" :
                                    j.status === "executing" ? "#a855f7" :
                                    j.status === "approved" ? "#a855f7" :
                                    j.status === "generating_media" ? "#f59e0b" :
                                    j.status === "pending_media_review" ? "#3b82f6" :
                                    isPending ? "#f59e0b" :
                                    "#888";
                                const created = new Date(j.createdAt).toLocaleString();
                                const showUpdateForm = updateFormId === j._id;
                                return (
                                    <div key={j._id} style={{
                                        background: "#0d0d1a",
                                        border: `1px solid ${isPending ? "#4a3a1a" : j.status === "pending_media_review" ? "#1a2a4a" : "#2a2a4a"}`,
                                        borderRadius: "6px",
                                        padding: "10px 12px",
                                    }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
                                            <div style={{ fontSize: "12px", color: statusColor, fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                                {j.status.replace(/_/g, " ")}
                                            </div>
                                            <div style={{ fontSize: "11px", color: "#555" }}>{created}</div>
                                        </div>
                                        {action && !isPending && (
                                            <div style={{ marginTop: "6px", fontSize: "12px", color: "#aaa" }}>
                                                <span style={{ color: "#777" }}>{action.actionType}</span>
                                                {action.description ? ` — ${action.description}` : ""}
                                            </div>
                                        )}
                                        {action && isPending && (
                                            <div style={{ marginTop: "10px" }}>
                                                {action.plan && (
                                                    <div style={{ fontSize: "12px", color: "#ccc", marginBottom: "8px", lineHeight: "1.6", padding: "8px", background: "#111128", borderRadius: "4px" }}>
                                                        {action.plan}
                                                    </div>
                                                )}
                                                <div style={{ fontSize: "12px", color: "#888", marginBottom: "4px" }}>
                                                    <span style={{ color: "#f59e0b", fontWeight: "bold" }}>{action.actionType}</span>
                                                    {action.description ? ` — ${action.description}` : ""}
                                                </div>
                                                {action.reasoning && (
                                                    <div style={{ fontSize: "11px", color: "#666", marginBottom: "10px", fontStyle: "italic" }}>
                                                        {action.reasoning}
                                                    </div>
                                                )}
                                                {action.actionType === "cron_job" && action.cronExpression && (
                                                    <div style={{ fontSize: "11px", color: "#555", marginBottom: "8px" }}>
                                                        Schedule: <span style={{ color: "#aaa" }}>{action.cronExpression}</span>
                                                    </div>
                                                )}
                                                {action.actionType === "create_asset" && action.content && (
                                                    <details style={{ marginBottom: "8px" }}>
                                                        <summary style={{ fontSize: "11px", color: "#555", cursor: "pointer" }}>Preview content</summary>
                                                        <pre style={{ fontSize: "11px", color: "#999", background: "#111128", padding: "8px", borderRadius: "4px", whiteSpace: "pre-wrap", maxHeight: "200px", overflow: "auto", marginTop: "4px" }}>
                                                            {action.content.slice(0, 1200)}{action.content.length > 1200 ? "..." : ""}
                                                        </pre>
                                                    </details>
                                                )}
                                                {action.actionType === "post_instagram_generated" && (
                                                    <div style={{ marginBottom: "8px" }}>
                                                        <div style={{ fontSize: "11px", color: "#555", marginBottom: "4px" }}>
                                                            {action.mediaType === "video" ? "Video" : "Image"} · Prompt:
                                                        </div>
                                                        <div style={{ fontSize: "11px", color: "#999", background: "#111128", padding: "8px", borderRadius: "4px", marginBottom: "6px" }}>
                                                            {action.prompt}
                                                        </div>
                                                        {action.voiceoverScript && (
                                                            <details>
                                                                <summary style={{ fontSize: "11px", color: "#555", cursor: "pointer", marginBottom: "4px" }}>Voiceover script</summary>
                                                                <div style={{ fontSize: "11px", color: "#999", background: "#111128", padding: "8px", borderRadius: "4px", whiteSpace: "pre-wrap" }}>
                                                                    {action.voiceoverScript}
                                                                </div>
                                                            </details>
                                                        )}
                                                    </div>
                                                )}
                                                {!showUpdateForm && (
                                                    <div style={{ display: "flex", gap: "8px", marginTop: "6px", flexWrap: "wrap" }}>
                                                        <button
                                                            onClick={() => handleApproveUI(j._id)}
                                                            disabled={approvingId === j._id}
                                                            style={{
                                                                padding: "5px 14px",
                                                                background: approvingId === j._id ? "#2a2a4a" : "#22c55e",
                                                                color: approvingId === j._id ? "#555" : "#fff",
                                                                border: "none",
                                                                borderRadius: "5px",
                                                                cursor: approvingId === j._id ? "not-allowed" : "pointer",
                                                                fontSize: "12px",
                                                                fontWeight: "bold",
                                                            }}
                                                        >
                                                            {approvingId === j._id ? "Approving..." : "Approve"}
                                                        </button>
                                                        <button
                                                            onClick={() => { setUpdateFormId(j._id); setUpdateText(""); }}
                                                            style={{
                                                                padding: "5px 14px",
                                                                background: "transparent",
                                                                color: "#f59e0b",
                                                                border: "1px solid #4a3a1a",
                                                                borderRadius: "5px",
                                                                cursor: "pointer",
                                                                fontSize: "12px",
                                                            }}
                                                        >
                                                            Request Update
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteJob(j._id)}
                                                            disabled={deletingJobId === j._id}
                                                            style={{
                                                                padding: "5px 14px",
                                                                background: "transparent",
                                                                color: deletingJobId === j._id ? "#555" : "#f87171",
                                                                border: `1px solid ${deletingJobId === j._id ? "#2a2a4a" : "#4a1a1a"}`,
                                                                borderRadius: "5px",
                                                                cursor: deletingJobId === j._id ? "not-allowed" : "pointer",
                                                                fontSize: "12px",
                                                            }}
                                                        >
                                                            {deletingJobId === j._id ? "Deleting..." : "Delete"}
                                                        </button>
                                                    </div>
                                                )}
                                                {showUpdateForm && (
                                                    <div style={{ marginTop: "8px" }}>
                                                        <textarea
                                                            value={updateText}
                                                            onChange={e => setUpdateText(e.target.value)}
                                                            rows={3}
                                                            placeholder="Describe what you'd like the agent to do differently..."
                                                            style={{
                                                                width: "100%",
                                                                background: "#111128",
                                                                border: "1px solid #4a3a1a",
                                                                borderRadius: "5px",
                                                                color: "#ddd",
                                                                padding: "8px",
                                                                fontSize: "12px",
                                                                resize: "vertical",
                                                                boxSizing: "border-box",
                                                            }}
                                                        />
                                                        <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                                                            <button
                                                                onClick={() => handleRequestUpdateUI(j._id)}
                                                                disabled={updatingId === j._id || !updateText.trim()}
                                                                style={{
                                                                    padding: "5px 14px",
                                                                    background: updatingId === j._id || !updateText.trim() ? "#2a2a4a" : "#f59e0b",
                                                                    color: updatingId === j._id || !updateText.trim() ? "#555" : "#000",
                                                                    border: "none",
                                                                    borderRadius: "5px",
                                                                    cursor: updatingId === j._id || !updateText.trim() ? "not-allowed" : "pointer",
                                                                    fontSize: "12px",
                                                                    fontWeight: "bold",
                                                                }}
                                                            >
                                                                {updatingId === j._id ? "Submitting..." : "Submit"}
                                                            </button>
                                                            <button
                                                                onClick={() => { setUpdateFormId(null); setUpdateText(""); }}
                                                                style={{
                                                                    padding: "5px 14px",
                                                                    background: "transparent",
                                                                    color: "#555",
                                                                    border: "1px solid #2a2a4a",
                                                                    borderRadius: "5px",
                                                                    cursor: "pointer",
                                                                    fontSize: "12px",
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {j.status === "pending_media_review" && (
                                            <div style={{ marginTop: "10px" }}>
                                                {action && (
                                                    <div style={{ fontSize: "12px", color: "#888", marginBottom: "8px" }}>
                                                        <span style={{ color: "#3b82f6", fontWeight: "bold" }}>{action.mediaType === "video" ? "Video" : "Image"}</span>
                                                        {" — "}{action.caption?.slice(0, 100)}{action.caption?.length > 100 ? "..." : ""}
                                                    </div>
                                                )}
                                                {j.mediaUrl && j.mediaType === "image" && (
                                                    <>
                                                        <img
                                                            src={j.mediaUrl}
                                                            alt="Generated"
                                                            style={{ maxWidth: "100%", maxHeight: "360px", borderRadius: "6px", display: "block", marginBottom: "4px", objectFit: "contain" }}
                                                        />
                                                        {j.falCostUsd != null && (
                                                            <div style={{ fontSize: "11px", color: "#888", marginBottom: "8px" }}>
                                                                fal.ai: <span style={{ color: "#f59e0b", fontFamily: "monospace" }}>${j.falCostUsd.toFixed(2)}</span>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                                {j.mediaUrl && j.mediaType === "video" && (
                                                    <>
                                                        <video
                                                            src={j.mediaUrl}
                                                            controls
                                                            style={{ maxWidth: "100%", maxHeight: "360px", borderRadius: "6px", display: "block", marginBottom: "4px" }}
                                                        />
                                                        {j.falCostUsd != null && (
                                                            <div style={{ fontSize: "11px", color: "#888", marginBottom: "8px" }}>
                                                                fal.ai: <span style={{ color: "#f59e0b", fontFamily: "monospace" }}>${j.falCostUsd.toFixed(2)}</span>
                                                                {j.falNumClips > 0 && <span style={{ color: "#555" }}> ({j.falNumClips} clip{j.falNumClips !== 1 ? "s" : ""} × $0.08)</span>}
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                                {action?.caption && (
                                                    <div style={{ fontSize: "11px", color: "#aaa", background: "#111128", borderRadius: "4px", padding: "8px", marginBottom: "6px", whiteSpace: "pre-wrap" }}>
                                                        <span style={{ color: "#555", display: "block", marginBottom: "2px" }}>Caption</span>
                                                        {action.caption}
                                                    </div>
                                                )}
                                                {action?.voiceoverScript && (
                                                    <div style={{ fontSize: "11px", color: "#aaa", background: "#111128", borderRadius: "4px", padding: "8px", marginBottom: "10px", whiteSpace: "pre-wrap" }}>
                                                        <span style={{ color: "#555", display: "block", marginBottom: "2px" }}>Voiceover</span>
                                                        {action.voiceoverScript}
                                                    </div>
                                                )}
                                                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                                    <button
                                                        onClick={() => handleApproveMediaUI(j._id)}
                                                        disabled={approvingMediaId === j._id}
                                                        style={{
                                                            padding: "5px 14px",
                                                            background: approvingMediaId === j._id ? "#2a2a4a" : "#22c55e",
                                                            color: approvingMediaId === j._id ? "#555" : "#fff",
                                                            border: "none",
                                                            borderRadius: "5px",
                                                            cursor: approvingMediaId === j._id ? "not-allowed" : "pointer",
                                                            fontSize: "12px",
                                                            fontWeight: "bold",
                                                        }}
                                                    >
                                                        {approvingMediaId === j._id ? "Posting..." : "Post to Instagram"}
                                                    </button>
                                                    <button
                                                        onClick={() => handleCancelJob(j._id)}
                                                        disabled={cancellingId === j._id}
                                                        style={{
                                                            padding: "5px 14px",
                                                            background: "transparent",
                                                            color: cancellingId === j._id ? "#555" : "#f87171",
                                                            border: `1px solid ${cancellingId === j._id ? "#2a2a4a" : "#4a1a1a"}`,
                                                            borderRadius: "5px",
                                                            cursor: cancellingId === j._id ? "not-allowed" : "pointer",
                                                            fontSize: "12px",
                                                        }}
                                                    >
                                                        {cancellingId === j._id ? "Cancelling..." : "Discard"}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MarketingAgent;
